<!-- One template for the header row and the data rows: without a `row` in its context it renders the header's leading cells. -->
<ng-template #leadingCells let-row="row" let-record="record">
    @if (showsChevronTrack()) {
    <span
        class="sh-hierarchy-table__cell sh-hierarchy-table__cell--sticky-start sh-hierarchy-table__cell--chevron"
        [class.sh-hierarchy-table__cell--sticky-start-edge]="!enableSelect()"
        [attr.role]="row ? cellRole() : 'columnheader'"
    >
        @if (row) {
            <!-- A row that cannot be opened leaves the slot empty rather than showing a dead chevron. -->
            @if (row.expandable) {
                <!-- No `aria-expanded` here: the row carries it, so a screen reader states it once. -->
                <button type="button" class="sh-pure-icon-button sh-hierarchy-table__chevron" (click)="expandToggled.emit(row)">
                    <svg sh-icon class="sh-icon-button__icon" [size]="iconSize.SMALL" [name]="row.expanded ? 'angle-down' : 'angle-right'" aria-hidden="true"></svg>
                    <span class="sh-visually-hidden">{{ (row.expanded ? "hierarchy.collapse" : "hierarchy.expand") | translate }}</span>
                </button>
            }
        } @else if (anyExpanded()) {
            <button
                type="button"
                class="sh-pure-icon-button sh-hierarchy-table__collapse-all"
                (click)="collapseAllRequested.emit()"
                [title]="'hierarchy.collapse-all' | translate"
            >
                <svg sh-icon class="sh-icon-button__icon" [size]="iconSize.SMALL" name="angle-double-up" aria-hidden="true"></svg>
                <span class="sh-visually-hidden">{{ "hierarchy.collapse-all" | translate }}</span>
            </button>
        }
    </span>
    }
    @if (enableSelect()) {
        <span
            class="sh-hierarchy-table__cell sh-hierarchy-table__cell--sticky-start sh-hierarchy-table__cell--sticky-start-edge sh-hierarchy-table__cell--checkbox"
            [style.left]="leadingTrack()"
            [attr.role]="row ? cellRole() : 'columnheader'"
        >
            @if (row) {
                @if (isRowSelectable(row)) {
                    <input
                        class="sh-checkbox"
                        type="checkbox"
                        [checked]="isRowSelected(row)"
                        (change)="selectRow(row, record, $event)"
                        [attr.aria-label]="'hierarchy.select-row' | translate"
                    />
                }
            } @else if (batchActionItems()?.length) {
                <!-- Clipped by the port it now shares with the rows — see the actions cell below for the escape. -->
                <sh-form-checkbox-with-dropdown
                    [id]="'hierarchy-selection-checkbox'"
                    [control]="selectAllCheckbox"
                    [canBeIndeterminate]="true"
                    [inverted]="false"
                    [inOverflownContainer]="true"
                    [options]="selectAllOptions"
                ></sh-form-checkbox-with-dropdown>
            } @else {
                <input
                    class="sh-checkbox"
                    type="checkbox"
                    [checked]="allSelected()"
                    [indeterminate]="someSelected()"
                    (change)="onSelectAllToggled($event)"
                    [attr.aria-label]="'hierarchy.select-page' | translate"
                />
            }
        </span>
    }
</ng-template>

<!--
    Shared by the column header, the stuk sub-header and a stuk cell's own accessible name, which differ only in
    what sits beside the label and in whether it is drawn at all. One template, so the three cannot drift.
-->
<ng-template #columnLabel let-column="column" let-hidden="hidden">
    <span class="sh-hierarchy-table__header__label" [class.sh-visually-hidden]="hidden || !column.columnDefinition.showHeader">
        @if (column.columnDefinition.label) {
            {{ column.columnDefinition.label }}
        } @else if (column.fieldDefinition; as fieldDefinition) {
            {{ fieldDefinition | translateFieldDefinition }}
        }
    </span>
</ng-template>

<div class="sh-hierarchy-table-stack">
    <!--
        The table itself. `treegrid` in a tree and `table` in a flat list — the one
        port between it and the rows is presentational, so the single rowgroup reads as its own child.
    -->
    <div class="sh-hierarchy-table-stack__grid" [attr.role]="gridRole()" [attr.aria-rowcount]="ariaRowcount()">
        <div
            #scrollport
            class="sh-hierarchy-table-wrapper"
            [class.sh-hierarchy-table-wrapper--can-scroll-end]="canScrollToEnd()"
            [class.sh-hierarchy-table-wrapper--scrolled-from-start]="isScrolledFromStart()"
            (scroll)="onScroll($event)"
            role="presentation"
        >
            @if (columns(); as tableColumns) {
                <div #grid class="sh-hierarchy-table" role="rowgroup">
                    <div class="sh-hierarchy-table__row sh-hierarchy-table__row--header" [style.grid-template-columns]="gridTemplate()" role="row">
                        <ng-container *ngTemplateOutlet="leadingCells"></ng-container>
                        @for (column of tableColumns; track column) {
                            <span class="sh-hierarchy-table__cell" role="columnheader">
                                <span class="sh-hierarchy-table__header">
                                    <ng-container *ngTemplateOutlet="columnLabel; context: { column: column }"></ng-container>
                                    @if (column.columnDefinition.sortable) {
                                        <sh-column-sort
                                            [columnDefinition]="getSortColumnDef(column)"
                                            [currentSort]="currentSort()"
                                            (sortInfoChanged)="sortChanged.emit($event)"
                                        ></sh-column-sort>
                                    }
                                    @if (hasColumnFilter(column)) {
                                        <sh-column-filter
                                            [field]="column.columnDefinition.key"
                                            [fieldType]="column.fieldDefinition?.Type"
                                            [value]="getColumnFilterValue(column)"
                                            [textFields]="getColumnFilterFields(column)"
                                            [enumFacet]="getEnumFacet(column)"
                                            [selectedEnumValues]="getSelectedEnumValues(column)"
                                            (valueChanged)="columnFilterChanged.emit({ field: column.columnDefinition.key, value: $event })"
                                            (fieldValueChanged)="columnFilterChanged.emit($event)"
                                            (opened)="onEnumFilterOpened(column)"
                                            (facetValueSelected)="facetValueSelected.emit($event)"
                                            (enumCleared)="onEnumFilterCleared(column)"
                                        ></sh-column-filter>
                                    }
                                </span>
                            </span>
                        }
                        @if (hasRowActions()) {
                            <span class="sh-hierarchy-table__cell sh-hierarchy-table__cell--actions sh-hierarchy-table__cell--sticky-end" role="columnheader">
                                {{ "ui.actions" | translate }}
                            </span>
                        }
                    </div>
                    @if (showRows()) {
                        @for (row of rows() ?? []; track row.key; let rowIndex = $index) {
                            <div
                                class="sh-hierarchy-table__row"
                                [class.sh-hierarchy-table__row--expanded]="row.expanded"
                                [class.sh-hierarchy-table__row--selected]="isRowSelected(row)"
                                [class.sh-hierarchy-table__row--subheader]="row.kind === 'subheader'"
                                [class.sh-hierarchy-table__row--submap]="row.kind === 'submap'"
                                [class.sh-hierarchy-table__row--grouped]="isInSubmapGroup(row)"
                                [style.grid-template-columns]="gridTemplateFor(row)"
                                [attr.data-row-key]="row.key"
                                [attr.data-indent]="indentSteps(row)"
                                role="row"
                                [attr.aria-hidden]="ariaHidden(row)"
                                [attr.aria-level]="ariaLevel(row)"
                                [attr.aria-expanded]="ariaExpanded(row)"
                                [attr.aria-busy]="ariaBusy(row)"
                                [attr.aria-posinset]="ariaPosinset(rowIndex)"
                                [attr.aria-setsize]="ariaSetsize(rowIndex)"
                                [attr.tabindex]="rowTabIndex(row, rowIndex)"
                                (keydown)="onRowKeydown($event, row)"
                                (focusin)="onRowFocusIn(row)"
                            >
                                @if (row.kind === "record") {
                                    @if (row.record$ | async; as record) {
                                        <ng-container *ngTemplateOutlet="leadingCells; context: { row: row, record: record }"></ng-container>
                                        @for (column of columnsFor(row); track column; let columnIndex = $index) {
                                            <!-- The name is inset by the track it shares with its dossier's, so it moves with the
                                                 caret and the checkbox rather than on its own. -->
                                            <span class="sh-hierarchy-table__cell" [attr.role]="cellRole()">
                                                <!--
                                                    A stuk's cells name themselves: the band carrying these labels is
                                                    hidden, and the header row above governs the dossier level's own,
                                                    different columns.
                                                -->
                                                @if (isNamedCellRow(row)) {
                                                    <ng-container *ngTemplateOutlet="columnLabel; context: { column: column, hidden: true }"></ng-container>
                                                }
                                                <!--
                                                    Where inside its dossier this record came from. Inside the cell rather than
                                                    above the row: it annotates this one row, and a row's children have to stay
                                                    cells for the treegrid semantics to hold.
                                                -->
                                                @if (isLeadCell(row, columnIndex) && row.parentCaption) {
                                                    <span class="sh-visually-hidden">{{ "hierarchy.parent-stuk" | translate }}</span>
                                                    <span class="sh-hierarchy-table__parent" [title]="row.parentCaption">{{ row.parentCaption }}</span>
                                                }
                                                @if (isLeadCell(row, columnIndex) && row.pathCaption) {
                                                    <!--
                                                        Named, so the path does not run into the title beside it. A
                                                        sibling rather than a child of the caption: that element's text
                                                        stays the path itself, which is what a copied selection gets.
                                                    -->
                                                    <span class="sh-visually-hidden">{{ "hierarchy.path" | translate }}</span>
                                                    <!--
                                                        Typeset for reading; the title stays the path itself. Hidden —
                                                        rather than dropped — in a grouped expansion: the submap header
                                                        above shows the folder once, and this row keeps announcing its
                                                        own path, so grouping costs a screen reader nothing. Also
                                                        hidden with "Toon submappen" off (`isPathCaptionHidden`),
                                                        where there is no header to fall back to at all.
                                                    -->
                                                    <span class="sh-hierarchy-table__path" [class.sh-visually-hidden]="isPathCaptionHidden(row)" [title]="row.pathCaption">{{
                                                        spacedPath(row.pathCaption)
                                                    }}</span>
                                                }
                                                <digihaven-cell
                                                    [record]="record"
                                                    [column]="column"
                                                    [overviewId]="overviewId()"
                                                    (rowNavigation)="rowNavigation.emit(record)"
                                                ></digihaven-cell>
                                            </span>
                                        }
                                        @if (hasRowActions()) {
                                            <span
                                                class="sh-hierarchy-table__cell sh-hierarchy-table__cell--actions sh-hierarchy-table__cell--sticky-end"
                                                [attr.role]="cellRole()"
                                                (click)="recordInteraction.emit(record)"
                                            >
                                                @if (rowActions()?.get(row.key) | async; as actionItem) {
                                                    <!--
                                                        The row's port is `overflow-x: auto`, which makes it a
                                                        scroll container in both axes — so an inline absolute
                                                        panel is clipped by it, and no z-index can escape that.
                                                        `inOverflownContainer` switches the panel to
                                                        `position: fixed`, positioned off the toggle's own rect
                                                        and re-following it on scroll.
                                                    -->
                                                    <sh-action-item [item]="actionItem" [inOverflownContainer]="true"></sh-action-item>
                                                }
                                            </span>
                                        }
                                    }
                                } @else if (row.kind === "subheader") {
                                    <!-- Labels only: a column filter acts on the page-wide query, so one here would claim to filter this dossier's children alone. -->
                                    <ng-container *ngTemplateOutlet="leadingCells; context: { row: row }"></ng-container>
                                    @for (column of columnsFor(row); track column) {
                                        <span class="sh-hierarchy-table__cell" [attr.role]="cellRole()">
                                            <ng-container *ngTemplateOutlet="columnLabel; context: { column: column }"></ng-container>
                                        </span>
                                    }
                                    @if (hasRowActions()) {
                                        <!-- Empty, but present: the sticky cell is what keeps scrolled content from
                                             showing through the band's trailing track. -->
                                        <span class="sh-hierarchy-table__cell sh-hierarchy-table__cell--actions sh-hierarchy-table__cell--sticky-end" [attr.role]="cellRole()"></span>
                                    }
                                } @else if (row.kind === "submap") {
                                    <!--
                                        Names the folder the stukken below share, once instead of on each of them.

                                        Not the structural span the other tree rows use: the caption starts at the
                                        checkbox track, so it lines up with the checkboxes rather than with the names —
                                        and the chevron track stays a sticky cell of its own, because the trunk this row
                                        opens has to be drawn inside one or the opaque leading cells paint over it.
                                    -->
                                    <span
                                        class="sh-hierarchy-table__cell sh-hierarchy-table__cell--sticky-start sh-hierarchy-table__cell--chevron"
                                        [class.sh-hierarchy-table__cell--sticky-start-edge]="!enableSelect()"
                                        [attr.role]="cellRole()"
                                    ></span>
                                    @if (enableSelect()) {
                                        <!--
                                            Nothing to show and everything to line up: this stands where the checkbox
                                            cell stands on every other row, purely so the start scroll shadow is drawn at
                                            the same offset here as there and the band reads as one unbroken line. It is
                                            transparent and sits below the caption, which overlaps it.
                                        -->
                                        <span
                                            class="sh-hierarchy-table__cell sh-hierarchy-table__cell--submap-shadow sh-hierarchy-table__cell--sticky-start-edge"
                                            [style.left]="leadingTrack()"
                                            aria-hidden="true"
                                        ></span>
                                    }
                                    <span class="sh-hierarchy-table__cell" [style.grid-column]="submapSpan" [attr.role]="cellRole()">
                                        <!-- The same caption a row carries, typeset the same way: plain text, the real path on hover. -->
                                        <span class="sh-hierarchy-table__path sh-hierarchy-table__path--submap" [title]="row.pathCaption">{{
                                            spacedPath(row.pathCaption)
                                        }}</span>
                                    </span>
                                    @if (hasRowActions()) {
                                        <!-- Empty, but present, exactly as the sub-header's is: it carries the end shadow. -->
                                        <span
                                            class="sh-hierarchy-table__cell sh-hierarchy-table__cell--actions sh-hierarchy-table__cell--sticky-end"
                                            [attr.role]="cellRole()"
                                        ></span>
                                    }
                                } @else {
                                    <!-- The structural kinds an expansion introduces. They live inside it, not beside the table. -->
                                    <ng-container *ngTemplateOutlet="leadingCells; context: { row: row }"></ng-container>
                                    <span
                                        class="sh-hierarchy-table__cell sh-hierarchy-table__cell--structural"
                                        [style.grid-column]="structuralSpan()"
                                        [attr.role]="cellRole()"
                                    >
                                        @switch (row.kind) {
                                            @case ("skeleton") {
                                                <span class="sh-hierarchy-table__skeleton" aria-hidden="true"></span>
                                            }
                                            @case ("show-more") {
                                                <button type="button" class="sh-link" (click)="moreChildrenRequested.emit(row.parentKey ?? '')">
                                                    {{ "hierarchy.show-more" | translate: { count: row.remaining ?? 0 } }}
                                                </button>
                                            }
                                            @case ("message") {
                                                <span class="sh-hierarchy-table__inline-message">
                                                    {{ (row.messageKey ?? "") | translate: row.messageParams }}
                                                    @if (row.canRetry) {
                                                        <button type="button" class="sh-link" (click)="moreChildrenRequested.emit(row.parentKey ?? '')">
                                                            {{ "hierarchy.retry" | translate }}
                                                        </button>
                                                    }
                                                </span>
                                            }
                                        }
                                    </span>
                                }
                            </div>
                        }
                    }
                </div>
            }
        </div>
    </div>

    <!-- The table's own states. Outside the grid, because a paragraph is not a row. -->
    @if (isLoading()) {
        <p class="sh-hierarchy-table__message">{{ "ui.loading" | translate }}</p>
    }
    @if (showEmptyState()) {
        <p class="sh-hierarchy-table__message">{{ emptyMessageKey() | translate }}</p>
    }
    @if (hasError()) {
        <p class="sh-hierarchy-table__message sh-hierarchy-table__message--error">
            {{ errorMessageKey() | translate }}
        </p>
    }

    <!--
        Expanding a dossier inserts its rows *after* the keypress that asked for them, so this is the only thing
        that tells a screen reader user what came of it. Always in the DOM — a region created along with its text
        is not reliably announced.
    -->
    <div class="sh-visually-hidden" role="status" aria-live="polite">
        @if (expansionAnnouncement(); as announcement) {
            {{ announcement.key | translate: { count: announcement.count, ...announcement.params } }}
        }
    </div>

    <!--
        Outside the grid, because a grid's children are rows and rowgroups and nothing else — an action bar
        inside it would be content a screen reader cannot place. One bar, fixed to the foot of the window rather
        than overlaying the header, so the header's own checkbox stays reachable while a selection is live and the
        bar is in reach from anywhere in a long result set. Its horizontal geometry is measured rather than
        declared — see `measureSelectionBar` — and `-sh-overlay` is undone in the stylesheet, which is what let
        two bars land on top of each other back when there were two.
    -->
    @if (showSelectionBar()) {
        <div
            class="sh-hierarchy-table-stack__bars"
            [style.left.px]="selectionBarBounds()?.left"
            [style.width.px]="selectionBarBounds()?.width"
        >
            <digihaven-action-bar
                [totalItemsCount]="selectionBarTotal()"
                [selectedItemsCount]="selectionBarCount()"
                [actionItems]="selectionBarActionItems()"
                [allSelected]="showBatchSelectionBar()"
                [darkMode]="true"
                [showSelectionCheckbox]="false"
                (shouldSelectAll)="toggleSelectAll($event)"
            ></digihaven-action-bar>
        </div>
    }
</div>
